import type { Except, Optional, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'

import { FileInput, type FileInputProps } from '../../forms/fileInput/FileInput'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { getWrapperProps } from '../formikUtils/getWrapperProps'

export type FormikFileInputProps = Except<
  FileInputProps,
  'onChange' | 'onTouched' | 'value' | 'wrapperProps'
> & {
  name: ValidatedKey
  isRequired?: boolean
  label: string
  description?: string
}

export const FormikFileInput = ({
  name,
  isRequired,
  label,
  description,
  disabled,
  ...restProps
}: FormikFileInputProps) => {
  const [field, meta, { setValue, setTouched }] =
    useField<Optional<File[]>>(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const wrapperProps = getWrapperProps({
    label,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      htmlFor: name,
      error,
    },
  })

  return (
    <FileInput
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      disabled={isFieldDisabled}
      {...field}
      {...restProps}
      onChange={(files) => setValue(files)}
      onTouched={() => setTouched(true)}
    />
  )
}
